import React, { useCallback } from 'react';
import { styled } from '@compiled/react';
import Badge from '@atlaskit/badge';
import Button from '@atlaskit/button';
import CheckCircleIcon from '@atlaskit/icon/glyph/check-circle';
import { colors } from '@atlaskit/theme';
import { token } from '@atlaskit/tokens';
import { useIntl } from '@atlassian/jira-intl';
import { useMappingState, useNumVisibleUnmappedColumns } from '../../../controllers/import-state';
import messages from './messages';

const SUCCESS_COLOR = token('color.icon.accent.green', colors.G400);

type Props = {
	hasValidationErrors: boolean;
};

export const UnmappedColumnsBanner = ({ hasValidationErrors }: Props) => {
	const { formatMessage } = useIntl();
	const [numVisibleUnmappedColumns, { setNextSelectedUnmappedColumn }] =
		useNumVisibleUnmappedColumns();
	const [mappingState] = useMappingState();

	const onClick = useCallback(() => {
		setNextSelectedUnmappedColumn();
	}, [setNextSelectedUnmappedColumn]);

	if (!mappingState || (hasValidationErrors && numVisibleUnmappedColumns === 0)) {
		return <div />;
	}

	if (numVisibleUnmappedColumns === 0) {
		return (
			<CompletedWrapper data-testid="project-create-importer-upload-flow.ui.mapping-screen.unmapped-columns-banner.completed-wrapper">
				<CheckCircleIcon primaryColor={SUCCESS_COLOR} label="" />
				<span>{formatMessage(messages.allFieldsMapped)}</span>
			</CompletedWrapper>
		);
	}

	return (
		<Wrapper>
			<Badge appearance="primary">{numVisibleUnmappedColumns}</Badge>
			<span>{formatMessage(messages.unmappedColumns)}</span>
			<Button onClick={onClick} appearance="link">
				{formatMessage(messages.goToNext)}
			</Button>
		</Wrapper>
	);
};

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const Wrapper = styled.div({
	display: 'flex',
	alignItems: 'center',
	flexWrap: 'wrap',
	padding: `${token('space.050', '4px')} ${token('space.150', '12px')}`,
	marginRight: token('space.100', '8px'),
	boxShadow: `${token('elevation.shadow.raised', '0px 1px 1px rgba(9, 30, 66, 0.25)')}`,
	borderRadius: '4px',
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766
	border: `1px solid ${token('color.border', colors.N30)}`,
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors -- Ignored via go/DSP-18766
	'> span': {
		marginLeft: token('space.100', '8px'),
	},
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const CompletedWrapper = styled.div({
	display: 'flex',
	alignItems: 'center',
	padding: `${token('space.050', '4px')} ${token('space.150', '12px')} ${token(
		'space.050',
		'4px',
	)} ${token('space.050', '4px')}`,
	marginRight: token('space.100', '8px'),
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766
	backgroundColor: `${token('color.background.accent.green.subtler', colors.G50)}`,
	minHeight: '42px',
	boxSizing: 'border-box',
	borderRadius: '4px',
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors -- Ignored via go/DSP-18766
	'> span': {
		marginLeft: token('space.100', '8px'),
	},
});
